'use client';

import { useTranslations } from 'next-intl';
import type { RelatedEvent } from '@/lib/api/event/models';
import { CircularProgress } from '@/lib/components/EventCard/EventLineCard';
import { Link } from '@/lib/routing';
import { Icon } from '@/lib/ui-kit/icons';
import { UiImage } from '@/lib/ui-kit/ui-image';
import { formatNumber } from '@/lib/utils/formatNumber';

interface RelatedEventRowProps {
  event: RelatedEvent;
}

export function RelatedEventRow({ event }: RelatedEventRowProps) {
  const tCard = useTranslations('event.card');

  return (
    <div className="group relative @container flex w-full items-center justify-between gap-3 rounded-[10px] py-2 transition-colors">
      <Link
        href={`/event/${event.slug}`}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute inset-0 z-[1]"
        aria-label={event.title}
        data-nav-source="related"
      />
      <div className="flex min-w-0 items-center gap-4">
        <UiImage
          size="md"
          src={event.image || '/evfallback.webp'}
          fallback="/evfallback.webp"
          alt={event.title}
          round
        />
        <div className="flex min-w-0 flex-col justify-center gap-1">
          <p className="line-clamp-2 text-[15px] font-semibold leading-[19px] text-v2-text-primary transition-colors group-hover:text-primary">
            {event.title}
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 text-v2-text-secondary">
            {event.endDate && (
              <div className="flex items-center gap-1">
                <Icon name="Clock" size={16} />
                <span className="whitespace-nowrap text-[12px] leading-4 tracking-[-0.12px]">
                  {tCard('ends')}{' '}
                  {new Date(event.endDate)
                    .toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })
                    .toUpperCase()
                    .replace(/ /g, '-')}
                </span>
              </div>
            )}
            {!!event.volume && (
              <span className="whitespace-nowrap text-[12px] leading-4 tracking-[-0.12px]">
                ${formatNumber(event.volume, 0)} Vol.
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <div className="relative h-[50px] w-[68px] shrink-0">
          <div className="absolute left-[2px] top-0 flex w-[64px] flex-col items-center">
            <CircularProgress
              percentage={event.chance != null ? Math.round(event.chance * 100) : 0}
              size={64}
              strokeWidth={3}
            />
            {(event.outcomeName || event.resolutionName) && (
              <span className="mt-0.5 w-full overflow-hidden truncate text-center text-[12px] leading-[1.15] text-v2-text-secondary">
                {event.outcomeName || event.resolutionName}
              </span>
            )}
          </div>
        </div>
        <span className="hidden size-8 shrink-0 items-center justify-center text-v2-text-secondary transition-colors @min-[400px]:flex group-hover:text-v2-text-primary">
          <Icon name="ChevronRight" size={16} />
        </span>
      </div>
    </div>
  );
}
